import { NgTemplateOutlet } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Output,
  computed,
  inject,
  linkedSignal,
  signal,
} from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'

import { combineLatest } from 'rxjs'

import {
  FLX_MODAL_DATA,
  FlxButtonComponent,
  FlxFormControlModule,
  FlxIconModule,
  FlxModalComponent,
  FlxModalService,
} from '@flowx/angular-ui-toolkit'
import { CopyResourceReviewDependenciesComponent } from '@flowx/designer/shared/components/copy-resource/copy-resource-review-dependencies.component'
import { CopyResourceReviewResourcesComponent } from '@flowx/designer/shared/components/copy-resource/copy-resource-review-resources.component'
import { CopyResourceSelectDestinationComponent } from '@flowx/designer/shared/components/copy-resource/copy-resource-select-destination.component'
import { CopyResourceSendDataReviewErrorsComponent } from '@flowx/designer/shared/components/copy-resource/copy-resource-send-data-review-errors.component'
import { ResourceUsagesViewReferencesComponent } from '@flowx/designer/shared/components/resource-usages/resource-usages-view-references.component'
import { ResourceUsagesComponent } from '@flowx/designer/shared/components/resource-usages/resource-usages.component'
import { FlxSpinnerModule } from '@flowx/designer/shared/components/spinner'
import {
  CopyResourceFromType,
  CopyResourceStep,
} from '@flowx/designer/shared/model/copy-resource.model'
import {
  ResourceUsage,
  ResourceUsageView,
  resourceTypeReadable,
} from '@flowx/designer/shared/model/resource-usage.model'
import { CopyResourceService } from '@flowx/designer/shared/services/copy-resource.service'

@Component({
  selector: 'flx-copy-resource',
  templateUrl: 'copy-resource.component.html',
  styleUrls: ['copy-resource.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CopyResourceSelectDestinationComponent,
    FlxButtonComponent,
    FlxFormControlModule,
    FlxModalComponent,
    CopyResourceReviewDependenciesComponent,
    CopyResourceReviewResourcesComponent,
    FlxSpinnerModule,
    CopyResourceSendDataReviewErrorsComponent,
    FlxIconModule,
    NgTemplateOutlet,
    ResourceUsagesViewReferencesComponent,
  ],
  providers: [CopyResourceService],
  host: { class: 'flex' },
})
export class CopyResourceComponent {
  #flxModalService = inject(FlxModalService)
  #copyResourceService = inject(CopyResourceService)
  #data = inject(FLX_MODAL_DATA)

  @Output() dataEventEmitter = new EventEmitter<{ success: boolean; branchName?: string }>()

  // TEMPLATE IMPORTS
  readonly CopyResourceStep = CopyResourceStep
  readonly ResourceUsageView = ResourceUsageView

  resourceType = this.#data.params.resourceType
  resourceSubType = this.#data.params.resourceSubType || null
  successFn = this.#data.params.successFn
  usedResources = this.#copyResourceService.usedResources.value
  stepName = signal<CopyResourceStep>(this.#data.stepName)
  displayResourceType = computed(() => resourceTypeReadable[this.resourceType] || this.resourceType)

  navigationTree = [
    CopyResourceStep.SELECT_DESTINATION,
    CopyResourceStep.REVIEW_RESOURCES,
    CopyResourceStep.REVIEW_DEPENDENCIES,
    CopyResourceStep.COPY_RESOURCE,
  ]

  navigation = {
    next: 1,
    back: -1,
    current: 0,
    visited: [],
    increment: (): void => {
      this.navigation.next++
      this.navigation.back++
      this.navigation.current++
    },
    decrement: (): void => {
      this.navigation.next--
      this.navigation.back--
      this.navigation.current--
    },
  }

  initialStepData = null

  loading = this.#copyResourceService.loading

  applicationAndBranchControlsValid = toSignal(
    combineLatest([
      this.#copyResourceService.applicationFC.statusChanges,
      this.#copyResourceService.branchFC.statusChanges,
    ]),
    { initialValue: ['INVALID', 'INVALID'] }
  )

  reviewResourcesFormValid = toSignal(
    this.#copyResourceService.conflictingResourcesFA.statusChanges,
    { initialValue: 'VALID' }
  )

  applyToAllValueChanges = toSignal(this.#copyResourceService.applyToAllFC.valueChanges, {
    initialValue: null,
  })

  formInvalidCurrentStep = computed(() => {
    const stepName = this.stepName()
    const applicationAndBranchControlsValid = this.applicationAndBranchControlsValid()
    const reviewResourcesFormValid = this.reviewResourcesFormValid()

    switch (stepName) {
      case CopyResourceStep.SELECT_DESTINATION: {
        const applicationControlStatus = applicationAndBranchControlsValid[0]
        const branchControlStatus = applicationAndBranchControlsValid[1]
        return applicationControlStatus === 'INVALID' || branchControlStatus === 'INVALID'
      }

      case CopyResourceStep.REVIEW_RESOURCES: {
        return reviewResourcesFormValid === 'INVALID'
      }
    }
  })

  title = linkedSignal<string>(() => {
    const stepName = this.stepName()

    switch (stepName) {
      case CopyResourceStep.SELECT_DESTINATION:
        return 'Select Destination'
      case CopyResourceStep.REVIEW_RESOURCES:
        return 'Review Resource Identifiers Conflicts'
      case CopyResourceStep.REVIEW_DEPENDENCIES:
        return 'New Dependencies'
      default: {
        return ''
      }
    }
  })

  subtitle = signal('')

  showBack = computed(() => {
    const stepName = this.stepName()

    return (
      stepName === CopyResourceStep.REVIEW_RESOURCES ||
      stepName === CopyResourceStep.REVIEW_DEPENDENCIES
    )
  })

  totalUsedResources = computed(() => {
    const usedResources = this.usedResources()

    return usedResources?.length ?? 0
  })

  noNewResourcesToImport = computed(() => {
    const applyToAllValue = this.applyToAllValueChanges()
    const resourcesComparison = this.#copyResourceService.resourcesComparison()
    const stepName = this.stepName()

    const hasNoNewResources = resourcesComparison.totalNewResources === 0
    const allOptionsAreSetToTarget = applyToAllValue === CopyResourceFromType.TARGET
    const isReviewResourcesStep = stepName === CopyResourceStep.REVIEW_RESOURCES

    return hasNoNewResources && allOptionsAreSetToTarget && isReviewResourcesStep
  })

  constructor() {
    this.#copyResourceService.params.set(this.#data.params)
    this.#copyResourceService.sourceAppName = this.#data.sourceAppName
    this.#copyResourceService.resourceName = this.#data.resourceName
  }

  async goToNextStep(): Promise<void> {
    this.checkDataChange()
    const nextStep = this.navigationTree[this.navigation.next]
    this.navigation.increment()
    this.setInitialStepData()

    if (nextStep !== CopyResourceStep.COPY_RESOURCE) {
      const visited = this.navigation.visited.includes(nextStep)

      if (!visited) {
        await this.#copyResourceService.getDataBeforeStepNavigation(nextStep)
        this.navigation.visited.push(nextStep)
      }

      const nextStepHasData = this.#copyResourceService.stepHasData(nextStep)

      if (nextStepHasData) {
        this.stepName.set(nextStep)
      } else {
        this.goToNextStep()
      }
    } else {
      this.stepName.set(nextStep)
    }

    return Promise.resolve()
  }

  checkDataChange(): void {
    const currentStep = this.navigationTree[this.navigation.current]
    const currentStepData = this.#copyResourceService.getUserInfluencedStepData(currentStep)

    if (
      this.initialStepData &&
      JSON.stringify(currentStepData) !== JSON.stringify(this.initialStepData)
    ) {
      switch (currentStep) {
        case CopyResourceStep.SELECT_DESTINATION: {
          this.#copyResourceService.resetStepsData()
          this.navigation.visited = []
          break
        }

        case CopyResourceStep.REVIEW_RESOURCES: {
          this.#copyResourceService.resetReviewDependenciesData()

          this.navigation.visited = this.navigation.visited.filter(
            (item) => item !== CopyResourceStep.REVIEW_DEPENDENCIES
          )

          break
        }
      }
    }
  }

  goToPreviousStep(): void {
    const previousStep = this.navigationTree[this.navigation.back]
    this.navigation.decrement()
    this.setInitialStepData()

    if (previousStep !== CopyResourceStep.SELECT_DESTINATION) {
      const previousStepHasData = this.#copyResourceService.stepHasData(previousStep)

      if (previousStepHasData) {
        this.stepName.set(previousStep)
      } else {
        this.goToPreviousStep()
      }
    } else {
      this.stepName.set(previousStep)
    }
  }

  setInitialStepData(): void {
    const currentStep = this.navigationTree[this.navigation.current]
    this.initialStepData = this.#copyResourceService.getUserInfluencedStepData(currentStep)
  }

  openResourceUsagesModal(resourceUsages: ResourceUsage[]): void {
    this.#flxModalService.show(
      ResourceUsagesComponent,
      {},
      {
        name: this.#data.resourceName,
        resourceType: this.resourceType,
        viewType: ResourceUsageView.USED_RESOURCES,
        resourceUsages,
      }
    )
  }

  closeModal(): void {
    this.#flxModalService.hide()
  }

  onCopySuccess(): void {
    const branchName = this.#copyResourceService.branchFC.value?.branchName
    this.dataEventEmitter.emit({ success: true, branchName })
    this.#flxModalService.hide()
  }
}
